
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Instagram,
  Linkedin,
  MessageCircle,
  ArrowUpRight,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  House,
  Building2,
  Paintbrush,
  Layers3,
} from "lucide-react";
import Logo from "../../assests/cubelogo.webp";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const socialLinks = [
    {
      name: "WhatsApp",
      icon: MessageCircle,
      href: "https://wa.me/919876543210",
    },
    {
      name: "Instagram",
      icon: Instagram,
      href: "https://www.instagram.com/cube4spaces/",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: "https://www.linkedin.com/company/cube4spaces/",
    },
  ];

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Our Portfolio", href: "/#portfolio" },
    { name: "Contact Us", href: "/contact" },
  ];

  const services = [
    { name: "Interior Design", icon: House, href: "/" },
    { name: "Construction", icon: Building2, href: "/" },
    { name: "Painting & Finishing", icon: Paintbrush, href: "/" },
    { name: "Turnkey Solutions", icon: Layers3, href: "/" },
  ];

  const handleSubscribe = (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setMessage("Please enter your email address.");
      return;
    }

    setMessage(
      "Thanks for your interest! Newsletter signup needs to be connected."
    );
  };

  return (
    <footer className="w-full bg-[#f7f6f3] py-8 sm:py-12 lg:py-16">
      {/* Full-width footer layout */}
      <div className="w-full">
        <div className="grid w-full grid-cols-1 overflow-hidden border-y border-stone-200 bg-white lg:grid-cols-[0.85fr_1.65fr] xl:grid-cols-[0.9fr_1.7fr]">
          {/* Brand Panel */}
          <div className="relative flex min-h-[400px] flex-col justify-between overflow-hidden bg-[#211f1d] p-6 text-white sm:min-h-[430px] sm:p-9 md:p-12 lg:min-h-[600px] lg:p-10 xl:p-14">
            {/* Decorative circles */}
            <div className="pointer-events-none absolute -right-24 -top-20 h-64 w-64 rounded-full border border-white/[0.08]" />
            <div className="pointer-events-none absolute -right-12 -top-8 h-44 w-44 rounded-full border border-white/[0.08]" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-blue-700/15 blur-[70px]" />

            <div className="relative z-10">
              {/* Brand */}
              <Link
                to="/"
                className="inline-flex items-center gap-3"
                aria-label="Cube4Spaces home"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 sm:h-14 sm:w-14">
                  <img
                    src={Logo}
                    alt="Cube4Spaces logo"
                    className="h-full w-full object-contain"
                  />
                </span>

                <span>
                  <span className="block font-['Poppins',sans-serif] text-lg font-bold tracking-tight text-white sm:text-xl">
                    CUBE4SPACES<span className="text-blue-400">.</span>
                  </span>

                  <span className="mt-1 block font-['DM_Sans',sans-serif] text-[9px] font-medium uppercase tracking-[2px] text-stone-400 sm:text-[10px]">
                    Design · Build · Handover
                  </span>
                </span>
              </Link>

              {/* Brand message */}
              <div className="mt-14 max-w-[430px] sm:mt-20 lg:mt-24">
                <p className="mb-4 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[3px] text-blue-400 sm:text-xs">
                  Spaces with purpose
                </p>

                <h2 className="font-['Poppins',sans-serif] text-[32px] font-semibold leading-[1.2] tracking-[-1.2px] text-white sm:text-4xl md:text-[42px] xl:text-5xl">
                  Your vision.
                  <br />
                  Our craft.
                  <br />
                  <span className="text-blue-400">Built together.</span>
                </h2>

                <p className="mt-5 max-w-[370px] font-['DM_Sans',sans-serif] text-sm leading-7 text-stone-300 sm:text-base">
                  From thoughtful interiors to complete construction, we bring
                  your ideas to life with care, quality, and attention to detail.
                </p>

                <Link
                  to="/contact"
                  className="group mt-7 inline-flex items-center gap-3 rounded-full bg-white px-5 py-3 font-['DM_Sans',sans-serif] text-sm font-bold text-stone-950 transition-all duration-300 hover:bg-blue-500 hover:text-white sm:mt-8"
                >
                  Start Your Project
                  <ArrowUpRight
                    size={17}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              </div>
            </div>

            {/* Social links */}
            <div className="relative z-10 mt-12 border-t border-white/10 pt-6 sm:mt-14 sm:pt-8">
              <p className="mb-4 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-stone-400 sm:text-xs">
                Connect with us
              </p>

              <div className="flex flex-wrap items-center gap-3">
                {socialLinks.map((item) => {
                  const Icon = item.icon;

                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.name}
                      title={item.name}
                      className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] text-white transition-all duration-300 hover:-translate-y-1 hover:border-blue-400 hover:bg-blue-500 sm:h-12 sm:w-12"
                    >
                      <Icon size={19} strokeWidth={1.7} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Information Panel */}
          <div className="flex min-w-0 flex-col justify-between p-6 sm:p-9 md:p-12 lg:p-8 xl:p-12 2xl:p-16">
            <div className="grid grid-cols-1 gap-9 sm:grid-cols-2 sm:gap-x-10 sm:gap-y-12 xl:grid-cols-3 xl:gap-x-8">
              {/* Quick Links */}
              <div>
                <p className="mb-5 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-blue-700 sm:mb-6 sm:text-xs">
                  Explore
                </p>

                <h3 className="mb-5 font-['Poppins',sans-serif] text-xl font-semibold tracking-tight text-stone-950 sm:text-2xl">
                  Quick links
                </h3>

                <ul className="space-y-3.5">
                  {quickLinks.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="group inline-flex items-center gap-2 font-['DM_Sans',sans-serif] text-sm font-medium text-stone-600 transition-colors duration-200 hover:text-blue-700 sm:text-[15px]"
                      >
                        <span>{item.name}</span>
                        <ArrowUpRight
                          size={14}
                          className="opacity-0 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div>
                <p className="mb-5 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-blue-700 sm:mb-6 sm:text-xs">
                  What We Do
                </p>

                <h3 className="mb-5 font-['Poppins',sans-serif] text-xl font-semibold tracking-tight text-stone-950 sm:text-2xl">
                  Our services
                </h3>

                <ul className="space-y-4">
                  {services.map((service) => {
                    const Icon = service.icon;

                    return (
                      <li key={service.name}>
                        <Link
                          to={service.href}
                          className="group flex items-center gap-3"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-600 transition-colors duration-200 group-hover:bg-blue-100 group-hover:text-blue-700">
                            <Icon size={17} strokeWidth={1.7} />
                          </span>

                          <span className="font-['DM_Sans',sans-serif] text-sm font-medium text-stone-600 transition-colors group-hover:text-blue-700 sm:text-[15px]">
                            {service.name}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Contact Details */}
              <div className="sm:col-span-2 xl:col-span-1">
                <p className="mb-5 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-blue-700 sm:mb-6 sm:text-xs">
                  Get in touch
                </p>

                <h3 className="mb-5 font-['Poppins',sans-serif] text-xl font-semibold tracking-tight text-stone-950 sm:text-2xl">
                  Let’s connect.
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700">
                      <MapPin size={17} strokeWidth={1.7} />
                    </span>

                    <div>
                      <p className="font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-800">
                        Visit our studio
                      </p>

                      <p className="mt-1 font-['DM_Sans',sans-serif] text-sm leading-6 text-stone-500">
                        Coimbatore, Tamil Nadu, India
                      </p>
                    </div>
                  </div>

                  <a
                    href="https://wa.me/919876543210"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700 transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                      <Phone size={17} strokeWidth={1.7} />
                    </span>

                    <span>
                      <span className="block font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-800">
                        Talk to our team
                      </span>
                      <span className="mt-1 block font-['DM_Sans',sans-serif] text-sm text-stone-500 transition-colors group-hover:text-blue-700">
                        WhatsApp us
                      </span>
                    </span>
                  </a>

                  <Link
                    to="/contact"
                    className="group flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700 transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                      <Mail size={17} strokeWidth={1.7} />
                    </span>

                    <span>
                      <span className="block font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-800">
                        Project enquiries
                      </span>
                      <span className="mt-1 block font-['DM_Sans',sans-serif] text-sm text-stone-500 transition-colors group-hover:text-blue-700">
                        Contact form
                      </span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-10 rounded-2xl border border-stone-200 bg-[#f8f7f4] p-5 sm:mt-12 sm:p-7 lg:mt-14">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-8">
                <div className="max-w-[390px]">
                  <p className="mb-2 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-blue-700 sm:text-xs">
                    Ideas & inspiration
                  </p>

                  <h3 className="font-['Poppins',sans-serif] text-xl font-semibold leading-snug tracking-tight text-stone-950 sm:text-2xl">
                    Good spaces start with good ideas.
                  </h3>

                  <p className="mt-2 font-['DM_Sans',sans-serif] text-sm leading-6 text-stone-500">
                    Stay connected for design inspiration and project updates.
                  </p>
                </div>

                <form
                  onSubmit={handleSubscribe}
                  className="w-full md:max-w-[390px]"
                >
                  <label
                    htmlFor="footer-email"
                    className="mb-2 block font-['DM_Sans',sans-serif] text-xs font-semibold text-stone-600"
                  >
                    Your email address
                  </label>

                  <div className="flex min-w-0 items-center gap-2 rounded-xl border border-stone-200 bg-white p-1.5 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                    <input
                      id="footer-email"
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setMessage("");
                      }}
                      placeholder="name@example.com"
                      autoComplete="email"
                      required
                      className="h-10 min-w-0 flex-1 bg-transparent px-2 font-['DM_Sans',sans-serif] text-sm text-stone-900 outline-none placeholder:text-stone-400 sm:px-3"
                    />

                    <button
                      type="submit"
                      className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-stone-950 px-3 font-['DM_Sans',sans-serif] text-xs font-bold text-white transition-colors hover:bg-blue-700 sm:px-4 sm:text-sm"
                    >
                      Subscribe
                      <ArrowRight size={15} />
                    </button>
                  </div>

                  {message && (
                    <p
                      role="status"
                      className="mt-2 font-['DM_Sans',sans-serif] text-xs leading-5 text-stone-600"
                    >
                      {message}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width bottom bar */}
        <div className="flex w-full flex-col items-center justify-between gap-3 px-5 py-6 text-center sm:flex-row sm:px-8 sm:text-left md:px-12 md:py-8 lg:px-16 xl:px-20">
          <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-stone-500 sm:text-sm">
            © {currentYear} Cube4Spaces. All rights reserved.
          </p>

          <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-stone-500 sm:text-sm">
            Designed with purpose. Built with care.
          </p>

          <a
            href="#top"
            onClick={(event) => {
              event.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 font-['DM_Sans',sans-serif] text-xs font-bold text-stone-700 transition-colors hover:text-blue-700 sm:text-sm"
          >
            Back to top
            <ArrowUpRight size={15} />
          </a>
        </div>
      </div>
    </footer>
  );
}
